
import React, { useState, useRef, useEffect } from 'react';
import { Camera, X, RefreshCw, Search, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { identifyProductFromImage } from '../services/geminiService';
import { storage } from '../lib/storage';
import { Product } from '../types';

interface VisualSearchModalProps {
  onClose: () => void;
  onProductFound: (product: Product) => void;
}

const VisualSearchModal: React.FC<VisualSearchModalProps> = ({ onClose, onProductFound }) => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      setStream(mediaStream);
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      setError("Camera access denied. Please enable permissions.");
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const captureAndAnalyze = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const base64Data = canvas.toDataURL('image/jpeg', 0.8).split(',')[1];

    setIsAnalyzing(true);
    setError(null);

    const inventory = storage.getProducts();
    const resultId = await identifyProductFromImage(base64Data, inventory);

    setIsAnalyzing(false);

    if (resultId && resultId !== 'NO_MATCH') {
      const found = inventory.find(p => p.id === resultId);
      if (found) {
        onProductFound(found);
        onClose();
      } else {
        setError("AI found something similar, but it's not in our catalog.");
      }
    } else {
      setError("AI couldn't identify this gadget. Try another angle!");
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90 backdrop-blur-xl p-4">
      <div className="bg-white w-full max-w-lg rounded-[40px] overflow-hidden shadow-2xl animate-in zoom-in duration-300">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
           <div className="flex items-center gap-3 text-blue-600">
              <Sparkles className="h-5 w-5" />
              <h3 className="font-black uppercase tracking-widest text-xs">AI Visual Search</h3>
           </div>
           <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-all">
             <X className="h-5 w-5 text-gray-400" />
           </button>
        </div>

        <div className="relative aspect-square bg-black">
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center p-10 text-center space-y-4">
               <AlertCircle className="h-12 w-12 text-red-500" />
               <p className="text-white font-bold">{error}</p>
               <button onClick={startCamera} className="bg-white text-black px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest">Retry Camera</button>
            </div>
          ) : (
            <>
              <video 
                ref={videoRef} 
                autoPlay 
                playsInline 
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border-[40px] border-black/20 pointer-events-none">
                 <div className="w-full h-full border-2 border-white/50 rounded-3xl animate-pulse"></div>
              </div>
            </>
          )}
          
          {isAnalyzing && (
            <div className="absolute inset-0 bg-blue-600/40 backdrop-blur-md flex flex-col items-center justify-center space-y-4">
               <Loader2 className="h-12 w-12 text-white animate-spin" />
               <p className="text-white font-black uppercase tracking-[0.2em] text-xs">AI is scanning...</p>
            </div>
          )}
        </div>

        <div className="p-10 flex flex-col items-center gap-6">
           <p className="text-gray-400 text-xs font-bold text-center leading-relaxed">Point your camera at a gadget (e.g., Phone, Laptop) and let AI find it for you.</p>
           <button 
             onClick={captureAndAnalyze}
             disabled={isAnalyzing || !!error}
             className="w-20 h-20 bg-gray-900 rounded-full flex items-center justify-center text-white hover:scale-110 active:scale-90 transition-all shadow-2xl disabled:opacity-30 disabled:scale-100"
           >
             <Camera className="h-10 w-10" />
           </button>
        </div>
        
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>
  );
};

export default VisualSearchModal;
